/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/** Matches any `{{token}}` placeholder, named or positional. */
const PLACEHOLDER_PATTERN: RegExp = /\{\{[^{}]+\}\}/g;

/**
 * Substitutes values into a locale string's `{{token}}` placeholders, by
 * position.
 *
 * `Locale.getString` returns the raw template and the model layer has no
 * interpolation step, so every feature that renders a parameterized string
 * needs this. Matching on order rather than on token name is deliberate: EN
 * uses named `{{camelCase}}` tokens (docs/localise-manual-workflow.md), while
 * the ES/FR bundles still carry positional `{{0}}` / `{{1}}` until the next
 * Lokalise pull renames them from EN. Order is the one thing both formats
 * agree on.
 *
 * A placeholder with no corresponding value is left untouched rather than
 * blanked, so a template/argument mismatch is visible instead of silently
 * dropping text.
 *
 * @param template - Locale string, possibly containing `{{token}}` placeholders
 * @param values - Replacement values, in placeholder order
 * @returns The interpolated string
 */
export const interpolateLocaleTemplate = (
  template: string,
  ...values: string[]
): string => {
  let index: number = 0;
  return template.replaceAll(PLACEHOLDER_PATTERN, (token: string): string => {
    const value: string | undefined = values[index];
    index += 1;
    return value === undefined ? token : value;
  });
};
